import Vue from 'vue';
import VueRouter from 'vue-router';
import i18n from '@/plugins/i18n';

import { isAuthenticated, hasRoles } from '@/middleware';
import store from '@/store';
import PerformanceContent from '@/components/dashboard/content/PerformanceContent.vue';
import SanityContent from '@/components/dashboard/content/SanityContent.vue';
import NutritionContent from '@/components/dashboard/content/NutritionContent.vue';
import PcpContent from '@/components/dashboard/content/PcpContent.vue';
import SlaughterForecastContent from '@/components/dashboard/content/SlaughterForecastContent.vue';
import AlertContent from '@/components/dashboard/content/AlertContent.vue';

Vue.use(VueRouter);

export const routes = [
  // Base Routes
  {
    path: '/',
    name: 'Home',
    meta: { layout: true },
    component: () => import(/* webpackChunkName: "Menu" */ '@/views/HomeView.vue'),
    beforeEnter: (to, from, next) => {
      isAuthenticated(to, from, next, router);
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import(/* webpackChunkName: "login" */ '@/views/LoginView.vue'),
  },
  {
    path: '/profile',
    name: 'Profile',
    meta: { layout: true, module: 'profile' },
    component: () => import(/* webpackChunkName: "login" */ '@/views/ProfileView.vue'),
    beforeEnter: (to, from, next) => {
      isAuthenticated(to, from, next, router);
    },
  },
  // Broiler Chicken Routes
  {
    path: '/broiler/performance',
    name: 'BroilerPerformance',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Performance',
      icon: 'fa-solid fa-chart-line fa-fw',
      label: 'performance',
      nextPage: 'BroilerPerformanceDashboard',
    },
    component: () => import(/* webpackChunkName: "performance" */ '@/views/PerformanceView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Performance');
    },
  },
  {
    path: '/broiler/performance/dashboard/:id',
    name: 'BroilerPerformanceDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Performance',
      dashboard: PerformanceContent,
      label: 'performance',
      icon: 'fa-solid fa-chart-line fa-fw',
    },
    component: () =>
      import(/* webpackChunkName: "performanceDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Performance');
    },
  },
  {
    path: '/broiler/slaughter-prediction',
    name: 'BroilerSlaughterPrediction',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Predição',
      icon: 'fa-solid fa-table-list fa-fw',
      label: 'slaughter_forecast',
      nextPage: 'BroilerPcpDashboard',
    },
    component: () =>
      import(/* webpackChunkName: "slaughterPrediction" */ '@/views/SlaughterPredictionView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Predição');
    },
  },
  {
    path: '/broiler/slaughter-prediction/planning',
    name: 'BroilerSlaughterPredictionPlanning',
    meta: { layout: true, module: 'broiler', role: 'frangos_de_corte', permission: 'Predição', nextPage: 'BroilerSlaughterForecastDashboard' },
    component: () =>
      import(
        /* webpackChunkName: "slaughterPredictionPlanning" */ '@/views/SlaughterPredictionPlanningView.vue'
      ),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Predição');
    },
  },
  {
    path: '/broiler/slaughter-prediction/dashboard/:id',
    name: 'BroilerSlaughterPredictionDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Predição',
      dashboard: SlaughterForecastContent,
      label: 'slaughter_forecast',
      icon: 'fa-solid fa-table-list',
      disabled: true,
    },
    component: () =>
      import(/* webpackChunkName: "slaughterPredictionDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Predição');
    },
  },
  {
    path: '/broiler/slaughter-prediction/planning/:id/:last_batch_id',
    name: 'BroilerSlaughterForecastDashboard',
    meta: {
      layout: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Predição',
      dashboard: SlaughterForecastContent,
      label: 'slaughter_forecast',
      icon: 'fa-solid fa-table-list',
    },
    component: () =>
      import(/* webpackChunkName: "SlaughterForecastDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Predição');
    },
  },
  {
    path: '/broiler/pcp/dashboard/:id',
    name: 'BroilerPcpDashboard',
    meta: {
      layout: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Predição',
      dashboard: PcpContent,
      label: 'pcp',
      icon: 'fa-solid fa-table-list',
    },
    component: () =>
      import(/* webpackChunkName: "PcpDashboardDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Predição');
    },
  },
  {
    path: '/broiler/sanity',
    name: 'BroilerSanity',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Sanidade',
      icon: 'fa-solid fa-briefcase-medical fa-fw',
      label: 'sanity',
      nextPage: 'BroilerSanityDashboard',
    },
    component: () => import(/* webpackChunkName: "sanity" */ '@/views/SanityListView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Sanidade');
    },
  },
  {
    path: '/broiler/sanity/dashboard/:id',
    name: 'BroilerSanityDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Sanidade',
      dashboard: SanityContent,
      label: 'sanity',
      icon: 'fa-solid fa-briefcase-medical',
    },
    component: () => import(/* webpackChunkName: "sanityDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Sanidade');
    },
  },
  {
    path: '/broiler/nutrition',
    name: 'BroilerNutrition',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Nutrição',
      icon: 'fa-solid fa-utensils fa-fw',
      label: 'nutrition',
      nextPage: 'BroilerNutritionDashboard',
    },
    component: () => import(/* webpackChunkName: "nutrition" */ '@/views/NutritionListView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Nutrição');
    },
  },
  {
    path: '/broiler/nutrition/dashboard/:id',
    name: 'BroilerNutritionDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      permission: 'Nutrição',
      dashboard: NutritionContent,
      label: 'nutrition',
      icon: 'fa-solid fa-utensils',
    },
    component: () =>
      import(/* webpackChunkName: "nutritionDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte', 'Nutrição');
    },
  },
  {
    path: '/broiler/alerts/recent/',
    name: 'BroilerRecentAlerts',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      icon: 'fa-solid fa-exclamation-triangle fa-fw',
      label: 'alerts/recent',
    },
    component: () => import(/* webpackChunkName: "alerts" */ '@/views/RecentAlertsView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte');
    },
  },
  {
    path: '/broiler/alerts/recent/dashboard/:id',
    name: 'BroilerAlertDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      dashboard: AlertContent,
      label: 'alerts/recent',
      icon: 'fa-solid fa-exclamation-triangle',
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte');
    },
  },
  {
    path: '/broiler/alerts/history',
    name: 'BroilerAlertHistory',
    meta: {
      layout: true,
      menu: true,
      module: 'broiler',
      role: 'frangos_de_corte',
      icon: 'fa-solid fa-history fa-fw',
      label: 'alerts/history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsHistory" */ '@/views/AlertHistoryView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte');
    },
  },
  {
    path: '/broiler/alerts/history/dashboard/:id',
    name: 'BroilerAlertHistoryDashboard',
    meta: {
      layout: true,
      menu: false,
      module: 'broiler',
      role: 'frangos_de_corte',
      dashboard: AlertContent,
      label: 'alerts/history',
      icon: 'fa-solid fa-history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'frangos_de_corte');
    },
  },
  // Matrix Routes
  {
    path: '/matrix/performance',
    name: 'MatrixPerformance',
    meta: {
      list: true,
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Performance',
      icon: 'fa-solid fa-chart-line fa-fw',
      label: 'performance',
      nextPage: 'MatrixPerformanceDashboard',
    },
    component: () => import(/* webpackChunkName: "performance" */ '@/views/PerformanceView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Performance');
    },
  },
  {
    path: '/matrix/gender/performance',
    name: 'MatrixPerformanceGender',
    meta: {
      list: true,
      gender: true,
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Performance',
      icon: 'fa-solid fa-mars-and-venus fa-fw',
      label: 'performance-male-female',
      nextPage: 'MatrixPerformanceGenderDashboard',
    },
    component: () => import(/* webpackChunkName: "performance" */ '@/views/PerformanceView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Performance');
    },
  },
  {
    path: '/matrix/performance/dashboard/:id',
    name: 'MatrixPerformanceDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Performance',
      dashboard: PerformanceContent,
      label: 'performance',
      icon: 'fa-solid fa-chart-line fa-fw',
    },
    component: () =>
      import(/* webpackChunkName: "performanceDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Performance');
    },
  },
  {
    path: '/matrix/performance/gender/dashboard/:id',
    name: 'MatrixPerformanceGenderDashboard',
    meta: {
      gender: true,
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Performance',
      dashboard: PerformanceContent,
      label: 'performance-male-female',
      icon: 'fa-solid fa-mars-and-venus fa-fw',
    },
    component: () =>
      import(/* webpackChunkName: "performanceDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Performance');
    },
  },
  {
    path: '/matrix/sanity',
    name: 'MatrixSanity',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Sanidade',
      icon: 'fa-solid fa-briefcase-medical',
      label: 'sanity',
      nextPage: 'MatrixSanityDashboard',
    },
    component: () => import(/* webpackChunkName: "sanity" */ '@/views/SanityListView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Sanidade');
    },
  },
  {
    path: '/matrix/sanity/dashboard/:id',
    name: 'MatrixSanityDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Sanidade',
      dashboard: SanityContent,
      label: 'sanity',
      icon: 'fa-solid fa-briefcase-medical',
    },
    component: () => import(/* webpackChunkName: "sanityDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Sanidade');
    },
  },
  {
    path: '/matrix/nutrition',
    name: 'MatrixNutrition',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Nutrição',
      icon: 'fa-solid fa-utensils fa-fw',
      label: 'nutrition',
      nextPage: 'MatrixNutritionDashboard',
    },
    component: () => import(/* webpackChunkName: "nutrition" */ '@/views/NutritionListView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Nutrição');
    },
  },
  {
    path: '/matrix/nutrition/dashboard/:id',
    name: 'MatrixNutritionDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      permission: 'Nutrição',
      dashboard: NutritionContent,
      label: 'nutrition',
      icon: 'fa-solid fa-utensils',
    },
    component: () =>
      import(/* webpackChunkName: "nutritionDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro', 'Nutrição');
    },
  },
  {
    path: '/matrix/alerts/recent/',
    name: 'MatrixRecentAlerts',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      icon: 'fa-solid fa-exclamation-triangle fa-fw',
      label: 'alerts/recent',
    },
    component: () => import(/* webpackChunkName: "alerts" */ '@/views/RecentAlertsView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro');
    },
  },
  {
    path: '/matrix/alerts/recent/dashboard/:id',
    name: 'MatrixAlertDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      dashboard: AlertContent,
      label: 'alerts/recent',
      icon: 'fa-solid fa-exclamation-triangle',
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro');
    },
  },
  {
    path: '/matrix/alerts/history/',
    name: 'MatrixAlertHistory',
    meta: {
      layout: true,
      menu: true,
      module: 'matrix',
      role: 'matrizeiro',
      icon: 'fa-solid fa-history fa-fw',
      label: 'alerts/history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsHistory" */ '@/views/AlertHistoryView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro');
    },
  },
  {
    path: '/matrix/alerts/history/dashboard/:id',
    name: 'MatrixAlertHistoryDashboard',
    meta: {
      layout: true,
      menu: false,
      module: 'matrix',
      role: 'matrizeiro',
      dashboard: AlertContent,
      label: 'alerts/history',
      icon: 'fa-solid fa-history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'matrizeiro');
    },
  },
  // Laying Birds Routes
  {
    path: '/laying-birds/sanity',
    name: 'LayingBirdsSanity',
    meta: {
      layout: true,
      menu: true,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      permission: 'Sanidade',
      icon: 'fa-solid fa-briefcase-medical fa-fw',
      label: 'sanity',
      nextPage: 'LayingBirdsSanityDashboard',
    },
    component: () => import(/* webpackChunkName: "sanity" */ '@/views/SanityListView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras', 'Sanidade');
    },
  },
  {
    path: '/laying-birds/sanity/dashboard/:id',
    name: 'LayingBirdsSanityDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      permission: 'Sanidade',
      dashboard: SanityContent,
      label: 'sanity',
      icon: 'fa-solid fa-briefcase-medical',
    },
    component: () => import(/* webpackChunkName: "sanityDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras', 'Sanidade');
    },
  },
  {
    path: '/laying-birds/nutrition',
    name: 'LayingBirdsNutrition',
    meta: {
      layout: true,
      menu: true,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      permission: 'Nutrição',
      icon: 'fa-solid fa-utensils fa-fw',
      label: 'nutrition',
      nextPage: 'LayingBirdsNutritionDashboard',
    },
    component: () => import(/* webpackChunkName: "nutrition" */ '@/views/NutritionListView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras', 'Nutrição');
    },
  },
  {
    path: '/laying-birds/nutrition/dashboard/:id',
    name: 'LayingBirdsNutritionDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      permission: 'Nutrição',
      dashboard: NutritionContent,
      label: 'nutrition',
      icon: 'fa-solid fa-utensils',
    },
    component: () =>
      import(/* webpackChunkName: "nutritionDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras', 'Nutrição');
    },
  },
  {
    path: '/laying-birds/alerts/recent/',
    name: 'LayingBirdsRecentAlerts',
    meta: {
      layout: true,
      menu: true,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      icon: 'fa-solid fa-exclamation-triangle fa-fw',
      label: 'alerts/recent',
    },
    component: () => import(/* webpackChunkName: "alerts" */ '@/views/RecentAlertsView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras');
    },
  },
  {
    path: '/laying-birds/alerts/recent/dashboard/:id',
    name: 'LayingBirdsAlertDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      dashboard: AlertContent,
      label: 'alerts/recent',
      icon: 'fa-solid fa-exclamation-triangle',
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras');
    },
  },
  {
    path: '/laying-birds/alerts/history',
    name: 'LayingBirdsAlertHistory',
    meta: {
      layout: true,
      menu: true,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      icon: 'fa-solid fa-history fa-fw',
      label: 'alerts/history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsHistory" */ '@/views/AlertHistoryView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras');
    },
  },
  {
    path: '/laying-birds/alerts/history/dashboard/:id',
    name: 'LayingBirdsAlertHistoryDashboard',
    meta: {
      layout: true,
      menu: false,
      module: 'laying-birds',
      role: 'aves_poedeiras',
      dashboard: AlertContent,
      label: 'alerts/history',
      icon: 'fa-solid fa-history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'aves_poedeiras');
    },
  },
  // Swine Routes
  {
    path: '/swine/nutrition',
    name: 'SwineNutrition',
    meta: {
      layout: true,
      menu: true,
      module: 'swine',
      role: 'suinos',
      permission: 'Nutrição',
      icon: 'fa-solid fa-utensils fa-fw',
      label: 'nutrition',
      nextPage: 'SwineNutritionDashboard',
    },
    component: () => import(/* webpackChunkName: "nutrition" */ '@/views/NutritionListView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'suinos', 'Nutrição');
    },
  },
  {
    path: '/swine/nutrition/dashboard/:id',
    name: 'SwineNutritionDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'swine',
      role: 'suinos',
      permission: 'Nutrição',
      dashboard: NutritionContent,
      label: 'nutrition',
      icon: 'fa-solid fa-utensils',
    },
    component: () =>
      import(/* webpackChunkName: "nutritionDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'suinos', 'Nutrição');
    },
  },
  {
    path: '/swine/alerts/recent/',
    name: 'SwineRecentAlerts',
    meta: {
      layout: true,
      menu: true,
      module: 'swine',
      role: 'suinos',
      icon: 'fa-solid fa-exclamation-triangle fa-fw',
      label: 'alerts/recent',
    },
    component: () => import(/* webpackChunkName: "alerts" */ '@/views/RecentAlertsView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'suinos');
    },
  },
  {
    path: '/swine/alerts/recent/dashboard/:id',
    name: 'SwineAlertDashboard',
    meta: {
      layout: true,
      menu: true,
      module: 'swine',
      role: 'suinos',
      dashboard: AlertContent,
      label: 'alerts/recent',
      icon: 'fa-solid fa-exclamation-triangle',
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'suinos');
    },
  },
  {
    path: '/swine/alerts/history',
    name: 'SwineAlertHistory',
    meta: {
      layout: true,
      menu: true,
      module: 'swine',
      role: 'suinos',
      icon: 'fa-solid fa-history fa-fw',
      label: 'alerts/history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsHistory" */ '@/views/AlertHistoryView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'suinos');
    },
  },
  {
    path: '/swine/alerts/history/dashboard/:id',
    name: 'SwineAlertHistoryDashboard',
    meta: {
      layout: true,
      menu: false,
      module: 'swine',
      role: 'suinos',
      dashboard: AlertContent,
      label: 'alerts/history',
      icon: 'fa-solid fa-history',
      disabled: true,
    },
    component: () => import(/* webpackChunkName: "alertsDashboard" */ '@/views/DashboardView.vue'),
    beforeEnter: (to, from, next) => {
      hasRoles(to, from, next, router, store, 'suinos');
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.afterEach((to, from) => {
  const pathParts = to.path.split('/').filter((part) => part !== '');

  store.state.environmentFilter.category = pathParts[0];

  const translatedParts = pathParts.map((part) => i18n.t(part, { default: part }));
  const translatedPath = translatedParts.join(' - ');

  document.label = translatedPath ? `Trinovati - ${translatedPath}` : 'Trinovati';
});

export default router;
